
import { useCallback, useEffect, useId, useMemo, useRef } from "react";


const HISTORY_KEY = "__materialentnahme_back_layer";
const DEPTH_KEY = "__materialentnahme_depth";

type LayerKind = "dropdown" | "dialog" | "sheet" | "detail" | "workflow" | "page";

interface RegisteredLayer {
  id: string;
  kind: LayerKind;
  order: number;
  onBack: () => void;
  rearmAfterBack?: () => boolean;
}

const layers = new Map<string, RegisteredLayer>();
let orderCounter = 0;
let listenerInstalled = false;
let suppressedPopCount = 0;
let rootExitArmedUntil = 0;
let lastObservedHistoryDepth = 0;

function installGlobalListener(): void {
  if (listenerInstalled) return;
  listenerInstalled = true;
  const initial = historyState();
  lastObservedHistoryDepth = typeof initial[DEPTH_KEY] === "number" ? initial[DEPTH_KEY] as number : 0;

  window.addEventListener("popstate", (event) => {
    const state = typeof event.state === "object" && event.state !== null
      ? event.state as Record<string, unknown>
      : {};
    const previousDepth = lastObservedHistoryDepth;
    const nextDepth = typeof state[DEPTH_KEY] === "number" ? state[DEPTH_KEY] as number : 0;
    lastObservedHistoryDepth = nextDepth;

    if (suppressedPopCount > 0) {
      suppressedPopCount -= 1;
      return;
    }

    // Browser-/Maus-Vorwärts darf keinen PWA-Back-Handler auslösen. Die
    // geschlossenen React-Layer werden absichtlich nicht aus einem alten
    // History-Sentinel rekonstruiert; stattdessen wird der Vorwärtsschritt
    // einmal unterdrückt und der Browser auf den aktuellen UI-Stand
    // zurückgeführt. Dadurch bleibt der nächste echte Zurück-Klick korrekt.
    if (nextDepth > previousDepth) {
      suppressedPopCount += 1;
      window.history.back();
      return;
    }

    const top = [...layers.values()].sort((a, b) => b.order - a.order)[0];
    if (!top) {
      const standalone = window.matchMedia?.("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
      if (!standalone) return;
      const now = Date.now();
      if (rootExitArmedUntil > now) {
        rootExitArmedUntil = 0;
        return;
      }
      rootExitArmedUntil = now + 3000;
      window.history.pushState({ ...historyState(), __materialentnahme_root_guard: true }, "", window.location.href);
      window.dispatchEvent(new CustomEvent("materialentnahme:toast", { detail: { message: "Nochmal drücken zum Beenden" } }));
      return;
    }

    // Ein Layer wird nur bei einer echten Bewegung zu geringerer App-Tiefe
    // geschlossen. Gleich tiefe popstate-Ereignisse (z. B. Hash-/Browser-
    // Sonderfälle) dürfen die PWA-Hierarchie nicht rückwärts treiben.
    if (nextDepth >= previousDepth) return;

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    top.onBack();
    // Eine verbleibende hierarchische Ebene sofort wieder bewaffnen – noch vor
    // dem nächsten React-Render. Dadurch kann ein onBack, das z. B. eine
    // Abbruchabfrage öffnet, anschließend deren eigenen Sentinel sauber OBEN
    // auflegen. Ein verzögertes Rearm im requestAnimationFrame konnte die
    // Reihenfolge umdrehen und dadurch wieder einen zusätzlichen Back erzeugen.
    if (top.rearmAfterBack?.() && historyState()[HISTORY_KEY] !== top.id) {
      const current = historyState();
      const currentDepth = typeof current[DEPTH_KEY] === "number" ? current[DEPTH_KEY] as number : 0;
      window.history.pushState({ ...current, [HISTORY_KEY]: top.id, [DEPTH_KEY]: currentDepth + 1 }, "", window.location.href);
      lastObservedHistoryDepth = currentDepth + 1;
    }
    if (top.kind === "page" || top.kind === "detail" || top.kind === "workflow") {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      });
    }
  });
}

function historyState(): Record<string, unknown> {
  return typeof window.history.state === "object" && window.history.state !== null
    ? window.history.state as Record<string, unknown>
    : {};
}

export function requestBrowserBack(): void {
  window.history.back();
}

function normalizeCurrentLayerSentinel(id: string): void {
  const current = historyState();
  if (current[HISTORY_KEY] !== id) return;
  const nextTop = [...layers.values()].sort((a, b) => b.order - a.order)[0];
  const currentDepth = typeof current[DEPTH_KEY] === "number" ? current[DEPTH_KEY] as number : 0;
  // Der aktuelle History-Eintrag bleibt physisch bestehen. Seine technische
  // Tiefe darf deshalb NICHT künstlich dekrementiert werden: sonst liegen
  // mehrere aufeinanderfolgende Entries mit derselben Tiefe im Browser-Stack
  // und echte Back-Klicks werden als "gleich tief" ignoriert. Die semantische
  // Ebene wird lediglich auf den verbleibenden Top-Layer normalisiert.
  window.history.replaceState(
    {
      ...current,
      [HISTORY_KEY]: nextTop?.id,
      [DEPTH_KEY]: currentDepth,
    },
    "",
    window.location.href,
  );
  lastObservedHistoryDepth = currentDepth;
}

export function useBackLayer({
  open,
  layerKey,
  kind,
  onBack,
  cleanupOnProgrammaticClose = false,
  rearmAfterBack = false,
}: {
  open: boolean;
  layerKey: string;
  kind: LayerKind;
  onBack: () => void;
  cleanupOnProgrammaticClose?: boolean;
  rearmAfterBack?: boolean;
}): { requestClose: () => void } {
  const reactId = useId();
  const id = useMemo(() => `${layerKey}:${reactId}`, [layerKey, reactId]);
  const onBackRef = useRef(onBack);
  const rearmAfterBackRef = useRef(rearmAfterBack);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    rearmAfterBackRef.current = rearmAfterBack;
  }, [rearmAfterBack]);

  useEffect(() => {
    installGlobalListener();
    if (!open) {
      layers.delete(id);

      if (cleanupOnProgrammaticClose) {
        // Programmgesteuertes Schließen darf NIEMALS eine Browsernavigation
        // auslösen. Der aktuelle Sentinel wird nur logisch auf die verbleibende
        // oberste Ebene normalisiert.
        normalizeCurrentLayerSentinel(id);
      }
      return;
    }

    const registration: RegisteredLayer = {
      id,
      kind,
      order: ++orderCounter,
      onBack: () => onBackRef.current(),
      rearmAfterBack: () => rearmAfterBackRef.current,
    };
    layers.set(id, registration);

    const current = historyState();
    const currentLayerId = typeof current[HISTORY_KEY] === "string" ? current[HISTORY_KEY] as string : null;
    if (currentLayerId !== id) {
      // Nur dieselbe konkrete Hook-Instanz darf einen vorhandenen Sentinel
      // wiederverwenden. Verschiedene Sheets teilen absichtlich die Familie
      // "sheet", sind aber unterschiedliche semantische Ebenen und benötigen
      // jeweils einen eigenen Back-Schritt.
      const currentDepth =
        typeof current[DEPTH_KEY] === "number"
          ? current[DEPTH_KEY] as number
          : 0;
      window.history.pushState(
        {
          ...current,
          [HISTORY_KEY]: id,
          [DEPTH_KEY]: currentDepth + 1,
        },
        "",
        window.location.href
      );
      lastObservedHistoryDepth = currentDepth + 1;
    }

    // Neue echte Seiten-/Detail-/Workflow-Ebenen beginnen immer oben. Sheets,
    // Dialoge und Dropdowns besitzen eigene Scrollcontainer und setzen diese in
    // ihren zentralen Komponenten zurück, damit die darunterliegende Seite ihre
    // Scrollposition beim bloßen Öffnen eines Overlays nicht verliert.
    let frame = 0;
    if (kind === "page" || kind === "detail" || kind === "workflow") {
      frame = window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
    }

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      layers.delete(id);
      // Auch ein echter Unmount/Tab-Recovery darf keinen technisch toten
      // Sentinel als zusätzlichen Zurück-Schritt im Browser-Verlauf hinterlassen.
      if (cleanupOnProgrammaticClose) normalizeCurrentLayerSentinel(id);
    };
  }, [cleanupOnProgrammaticClose, id, kind, layerKey, open]);

  const requestClose = useCallback(() => {
    if (!open) return;

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const current = historyState();
    if (current[HISTORY_KEY] === id) {
      // Normales Zurück verhält sich exakt wie Android-/Browser-Zurück.
      window.history.back();
      return;
    }

    // Falls der Layer ausnahmsweise keinen aktuellen Sentinel besitzt,
    // schließen wir ihn direkt, ohne einen anderen History-Eintrag anzufassen.
    onBackRef.current();
    if (kind === "page" || kind === "detail" || kind === "workflow") {
      window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
    }
  }, [id, kind, open]);

  return { requestClose };
}

export function cleanupCurrentBackLayerWithoutClosing(): void {
  const current = historyState();
  if (!(HISTORY_KEY in current)) return;
  suppressedPopCount += 1;
  window.history.back();
}


export function returnToAppRoot(): void {
  // Programmgesteuerte interne Navigation darf niemals den echten Browser-
  // History-Stack traversieren. history.go(-depth) konnte über unsere
  // pushState-Sentinels hinaus bis zu einem echten Dokumenteintrag springen
  // und dadurch auf beliebigen PWA-Seiten einen weißen Vollreload auslösen.
  layers.clear();
  const current = historyState();
  const next = { ...current, [DEPTH_KEY]: 0, [HISTORY_KEY]: undefined, __materialentnahme_root_guard: true };
  window.history.replaceState(next, "", window.location.href);
  lastObservedHistoryDepth = 0;
  window.requestAnimationFrame(() => window.scrollTo({top:0,left:0,behavior:"auto"}));
}
